export const CONTRACT_ADDRESS = "TAf4C5L5hXDh4o3ZufeUni4LEQbH1LHrz7";
export const MARKET_ADDRESS = "TKiJQhpye72AaKRXQrJUKj5e2UY6jzeXej";

export default class CoolCats {
    constructor() {
        this._tw = null;
        this._contract = null;
        this._market = null;
    }

    async init(tronWeb) {
        this._tw = tronWeb
        this._contract = await this._tw.contract().at(CONTRACT_ADDRESS);
        this._market = await this._tw.contract().at(MARKET_ADDRESS);
        return this;
    }

    async name() {
        return this._call('name');
    }

    async symbol() {
        return this._call('symbol');
    }

    /**
     * @param {Number} count
     * 
     * @returns {Promise<Number>}
     */
    async mint(count) {
        await this._contract?.mintNFT(count).send({
            feeLimit: 1_000_000_000,
            callValue: 1500_000_000 * count,
            shouldPollResponse:false,
        });

        return [0x0];
    }

    async addBid(tokenId, price) {
        if (await this._tw.trx.getBalance(this._tw.defaultAddress.base58) < parseInt(price))
            throw new Error('balance too low');

        let userBidsCount = await this.getUserBidCount(this._tw.defaultAddress.base58);
        await this._market?.addBid(tokenId, price).send({
            feeLimit: 500_000_000,
            callValue: price,
            shouldPollResponse:false,
        });

        return this.waitFor(async () => {
            const newCount = await this.getUserBidCount(this._tw.defaultAddress.base58);
            if (newCount > userBidsCount) {
                const lastBid = await this.getBidIdFromUser(this._tw.defaultAddress.base58, newCount-1);
                return await this.getBid(lastBid);
            }

            return false;
        });
    }

    waitFor(op) {
        return new Promise((resolve, reject) => {
            let tries = 20;
            const timer = setInterval(async () => {
                try {
                    const result = await op();
                    if (result) {
                        clearInterval(timer);
                        resolve(result);
                        return;
                    }
                } catch(_){}
                finally {
                    tries--;
                    if (tries === 0) {
                        clearInterval(timer);
                        reject('Operation failed');
                        return;
                    }
                }
            }, 500);
        })
    }

    async cancelBid(bidId) {
        await this._market?.cancelBid(bidId).send({
            feeLimit: 500_000_000,
            callValue: 0,
            shouldPollResponse:false,
        })

        return this.waitFor(async () => {
            const bid = await this.getBid(bidId);
            return !bid.active;
        })
    }
    
    async acceptBid(tokenId, bidId) {
        await this._market?.acceptBid(tokenId, bidId).send({
            feeLimit: 1_000_000_000,
            callValue: 0,
            shouldPollResponse:false,
        })

        return this.waitFor(async () => {
            const bid = await this.getBid(bidId);
            return bid.payer === await this.ownerOf(tokenId);
        })
    }

    async getBid(bidId) {
        const bid = await this._mcall('bids', [bidId]);
        return await this._formatBid(bid);
    }

    async getBidIdFromToken(tokenId, index) {
        const id = await this._mcall('bidsByToken', [tokenId, index]);
        return this._convertNumber(id);
    }

    async getTokenBids(tokenId) {
        const lot = await this.getMarketLotInfo(tokenId);
        if (!lot.isForSale) return [];

        let items = [];
        for (let i = 0; i < lot.bidsCount; i++) {
            const id = await this.getBidIdFromToken(tokenId, i);
            items.push(await this.getBid(id));
        }

        return items;
    }

    async getBidIdFromUser(address, index) {
        const id = await this._mcall('bidsByUser', [address, index]);
        return this._convertNumber(id);
    }

    async getUserBidCount(address) {
        const count = await this._mcall('bidsCountByUser', [address])
        return this._convertNumber(count);
    }

    async getUserBids(address) {
        let items = [];
        const count = await this.getUserBidCount(address);
        for (let i = 0; i < count; i++) {
            const id = await this.getBidIdFromUser(address, i);
            items.push(await this.getBid(id));
        }

        return items;
    }

    _formatBid(bid) {
        return {
            id: this._convertNumber(bid.id),
            tokenId: this._convertNumber(bid.tokenId),
            price: this._convertNumber(bid.price),
            payer: this._tw.address.fromHex(bid.payer),
            active: bid.active,
            status: bid.status
        }
    }

    // async getBids

    /**
     * @param {string} tokenId 
     * @returns {Promise<Object>}
     */
    async getMarketLotInfo(tokenId) {
        const lot = await this._mcall('marketLots', [tokenId]);
        return {
            tokenId: this._convertNumber(lot.tokenId),
            isForSale: lot.isForSale,
            owner: await this._convertAddress(lot.owner),
            price: this._convertNumber(lot.price),
            bidsCount: this._convertNumber(lot.bidsCount),
            maxBidPrice: this._convertNumber(lot.maxBidPrice)
        }
    }

    /**
     * @param {string} tokenId
     * @param {string} price
     */
    async putOnMarket(tokenId, price) {
        await this._contract?.approve(MARKET_ADDRESS, tokenId).send({
            feeLimit:100_000_000,
            shouldPollResponse:false
        });

        try {
            await this.waitFor(async () => {
                const addr = await this._convertAddress(await this._call('getApproved', [tokenId]));
                return addr === MARKET_ADDRESS;
            }, 30)
        } catch(_){}

        await this._market?.putOnMarket(tokenId, price).send({
            feeLimit:100_000_000,
            shouldPollResponse:false
        });

        return this.waitFor(async () => {
            const offer = await this.getMarketLotInfo(tokenId);
            if (offer.isForSale)
                return offer;
            return false;
        })
    }

    /**
     * @param {string} tokenId
     * @param {string} newPrice
     */
    async changeLotPrice(tokenId, newPrice) {
        const oldOffer = await this.getMarketLotInfo(tokenId);
        await this._market?.changeLotPrice(tokenId, newPrice).send({
            feeLimit:100_000_000,
            shouldPollResponse:false
        })

        return this.waitFor(async () => {
            const newOffer = await this.getMarketLotInfo(tokenId);
            if (newOffer.price !== oldOffer.price)
                return newOffer;
            return false;
        })
    }

    /**
     * @param {string} tokenId
     */
     async withdrawFromMarket(tokenId) {
        await this._market?.withdrawFromMarket(tokenId).send({
            feeLimit:100_000_000,
            shouldPollResponse:false
        });

        return this.waitFor(async () => {
            const offer = this.getMarketLotInfo(tokenId);
            if (!offer.isForSale) 
                return true;
            return false;
        })
    }

    async buyFromMarket(tokenId) {
        const lot = await this.getMarketLotInfo(tokenId);
        if (await this._tw.trx.getBalance(this._tw.defaultAddress.base58) < lot.price)
            throw new Error('balance too low');

        await this._market?.buyFromMarket(tokenId).send({
            feeLimit:100_000_000,
            callValue: lot.price,
            shouldPollResponse:false
        });

        return this.waitFor(async () => await this.ownerOf(tokenId) === this._tw.defaultAddress.base58);
    }

    /**
     * @returns {Number}
     */
    async maxNftSupply() {
        const num = await this._call('MAX_NFT_SUPPLY');
        return this._convertNumber(num);
    }
    /**
     * @param {string} tokenId
     * @returns {Promise<string>}
     */
    async ownerOf(tokenId) {
        try {
            const addr = await this._call('ownerOf', [tokenId]);
            return this._convertAddress(addr);
        } catch (_) {
            return ''
        }
    }

    /**
     * @returns {Number}
     */
    async totalSupply() {
        const num = await this._call('totalSupply');
        return this._convertNumber(num);
    }

    async tokenByIndex(index) {
        const id = await this._call('tokenByIndex', [index]);
        return this._convertNumber(id);
    }
    
    async tokenOfOwnerByIndex(address, index) {
        const id = await this._call('tokenOfOwnerByIndex', [address, index]);
        return this._convertNumber(id);
    }

    async balanceOf(address) {
        const id = await this._call('balanceOf', [address]);
        return this._convertNumber(id);
    }

    async getUserTokens(owner) {
        const count = await this.balanceOf(owner);
        let tokens = [];
        for (let i = 0; i < count; i++) {
            const id = await this.tokenOfOwnerByIndex(owner, i);
            tokens.push(id);
        }

        return tokens;
    }

    async getUser(address) {
        const user = await this._call('users', [address]);
        return {
            isExists: user.isExists,
            level: this._convertNumber(user.level),
            wallet: this._convertNumber(user.wallet),
            poolId: this._convertNumber(user.poolId),
            mintedCount: this._convertNumber(user.mintedCount)
        }
    }

    async getPoolAddressById(id) {
        const address = await this._call('rewardPool', [id]);
        return this._convertAddress(address);
    }

    async getPool() {
        let pool = [];
        const poolSize = this._convertNumber(await this._call('poolSize'));
        for (let i = 1; i <= poolSize; i++) {
            const addr = await this._convertAddress(await this._call('rewardPool', [i]));
            pool.push(await this.getUser(addr));
        }

        return pool;
    }

    async withdrawReward() {
        await this._contract.withdrawToUser().send({
            feeLimit:100_000_000,
            shouldPollResponse:true
        })
    }

    async _call(method, args=[]) {
        if (this._contract === null) {
            throw Error("Contract object not inited");
        }
        return await this._contract[method](...args).call();
    }

    async _mcall(method, args=[]) {
        if (this._market === null) {
            throw Error("Contract object not inited");
        }
        return await this._market[method](...args).call();
    }

    async _convertAddress(addr) {
        return this._tw.address.fromHex(addr);
    }

    _convertNumber(bignum) {
        return this._tw.toDecimal(bignum._hex);
    }
}